import { Head, useAuth } from "zudoku/components";
import { useState } from "react";

export const UserManagementPage = () => {
  const auth = useAuth();
  const [showPopup, setShowPopup] = useState(false);
  const [email, setEmail] = useState("");
  const [mailtoLink, setMailtoLink] = useState("");

  // Extract user's company domain from token
  const getUserData = () => {
    if (!auth.isAuthenticated) return { userCompanyDomain: "unknown", userTier: "unknown", isSuperAdmin: false };
    
    // Get RBAC_role, tier, and company domain from JWT token payload
    const token = (auth as any).providerData?.accessToken || (auth.user as any)?.access_token;
    if (!token) {
      return { 
        userCompanyDomain: "unknown", 
        userTier: "unknown", 
        isSuperAdmin: false
      };
    }
    
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => 
        '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
      ).join(''));
      const payload = JSON.parse(jsonPayload);
      
      // Extract claims from token payload (check namespaced first, then non-namespaced)
      const userCompanyDomain = payload["https://api.securitydatapoint.com/user_company_domain"] || 
                                payload.user_company_domain || 
                                "unknown";
      const userTier = payload["https://api.securitydatapoint.com/tier"] || 
                       payload.tier || 
                       "unknown";
      const permission = payload["https://api.securitydatapoint.com/permission"] || 
                       payload.permission || 
                       null;
      
      return {
        userCompanyDomain,
        userTier,
        isSuperAdmin: permission === "superadmin"
      };
    } catch {
      return { 
        userCompanyDomain: "unknown", 
        userTier: "unknown", 
        isSuperAdmin: false
      };
    }
  };

  const { userCompanyDomain, userTier, isSuperAdmin } = getUserData();

  // Check if user has access (must be superadmin on Scale or Enterprise tier)
  const hasAccess = isSuperAdmin && (userTier === "scale" || userTier === "enterprise");

  const handleButtonClick = () => {
    setShowPopup(true);
    setEmail("");
    setMailtoLink("");
  };

  const handleEmailSubmit = () => {
    if (!email || !email.includes("@")) {
      alert("Please enter a valid email address");
      return;
    }

    const subject = encodeURIComponent(`create non-admin account for ${userCompanyDomain}`);
    const body = encodeURIComponent(email);
    const mailto = `mailto:create_reporter_request@securitydatapoint.com?subject=${subject}&body=${body}`;
    
    setMailtoLink(mailto);
  };

  const handleClosePopup = () => {
    setShowPopup(false);
    setEmail("");
    setMailtoLink("");
  };

  if (!auth.isAuthenticated) {
    return (
      <div style={{ padding: "40px", textAlign: "center" }}>
        <h1>User Management</h1>
        <p>Please log in to access this page.</p>
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div style={{ padding: "40px", textAlign: "center" }}>
        <h1>User Management</h1>
        <p style={{ color: "#dc2626", marginTop: "20px" }}>
          Access denied. This page is only available for superadmins on the Scale or Enterprise tier.
        </p>
      </div>
    );
  }

  return (
    <div style={{ padding: "40px", maxWidth: "1200px", margin: "0 auto" }}>
      <h1 style={{ marginBottom: "10px" }}>User Management</h1>
      <p style={{ color: "#6b7280", marginBottom: "30px" }}>
        Manage user accounts for your organization
      </p>

      <div style={{ 
        backgroundColor: "#f9fafb", 
        padding: "30px", 
        borderRadius: "8px",
        border: "1px solid #e5e7eb"
      }}>
        <h2 style={{ fontSize: "20px", marginBottom: "10px" }}>
          Secondary Non-Admin Accounts
        </h2>
        <p style={{ color: "#6b7280", marginBottom: "20px" }}>
          Request a read-only report viewer account for your team members who need access to reports
          but don't require administrative privileges.
        </p>
        
        <button
          onClick={handleButtonClick}
          style={{
            backgroundColor: "#2563eb",
            color: "white",
            padding: "12px 24px",
            border: "none",
            borderRadius: "6px",
            fontSize: "16px",
            fontWeight: "500",
            cursor: "pointer",
            transition: "background-color 0.2s"
          }}
          onMouseOver={(e) => e.currentTarget.style.backgroundColor = "#1d4ed8"}
          onMouseOut={(e) => e.currentTarget.style.backgroundColor = "#2563eb"}
        >
          Get secondary non-admin account
        </button>
      </div>

      {/* Popup Modal */}
      {showPopup && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: "white",
            padding: "30px",
            borderRadius: "8px",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
            maxWidth: "500px",
            width: "90%"
          }}>
            <h2 style={{ marginTop: 0, marginBottom: "20px" }}>
              Request Non-Admin Account
            </h2>
            
            {!mailtoLink ? (
              <>
                <p style={{ color: "#6b7280", marginBottom: "20px" }}>
                  Enter the email address for the new non-admin report viewer account:
                </p>
                
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="user@example.com"
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    fontSize: "16px",
                    marginBottom: "20px",
                    boxSizing: "border-box"
                  }}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      handleEmailSubmit();
                    }
                  }}
                />
                
                <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
                  <button
                    onClick={handleClosePopup}
                    style={{
                      padding: "10px 20px",
                      border: "1px solid #d1d5db",
                      borderRadius: "6px",
                      backgroundColor: "white",
                      color: "#374151",
                      fontSize: "16px",
                      cursor: "pointer",
                      transition: "background-color 0.2s"
                    }}
                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = "#f9fafb"}
                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = "white"}
                  >
                    Cancel
                  </button>
                  
                  <button
                    onClick={handleEmailSubmit}
                    style={{
                      padding: "10px 20px",
                      border: "none",
                      borderRadius: "6px",
                      backgroundColor: "#2563eb",
                      color: "white",
                      fontSize: "16px",
                      fontWeight: "500",
                      cursor: "pointer",
                      transition: "background-color 0.2s"
                    }}
                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = "#1d4ed8"}
                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = "#2563eb"}
                  >
                    Continue
                  </button>
                </div>
              </>
            ) : (
              <>
                <p style={{ color: "#16a34a", marginBottom: "20px", fontWeight: "500" }}>
                  ✓ Request prepared successfully!
                </p>
                
                <p style={{ color: "#6b7280", marginBottom: "20px" }}>
                  Click the button below to open your email client and send the request to our team.
                </p>
                
                <div style={{
                  backgroundColor: "#f9fafb",
                  padding: "15px",
                  borderRadius: "6px",
                  marginBottom: "20px",
                  fontSize: "14px"
                }}>
                  <div style={{ marginBottom: "8px" }}>
                    <strong>To:</strong> info@sdp.com
                  </div>
                  <div style={{ marginBottom: "8px" }}>
                    <strong>Subject:</strong> create non-admin account for {userCompanyDomain}
                  </div>
                  <div>
                    <strong>Email:</strong> {email}
                  </div>
                </div>
                
                <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
                  <button
                    onClick={handleClosePopup}
                    style={{
                      padding: "10px 20px",
                      border: "1px solid #d1d5db",
                      borderRadius: "6px",
                      backgroundColor: "white",
                      color: "#374151",
                      fontSize: "16px",
                      cursor: "pointer",
                      transition: "background-color 0.2s"
                    }}
                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = "#f9fafb"}
                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = "white"}
                  >
                    Close
                  </button>
                  
                  <a
                    href={mailtoLink}
                    style={{
                      padding: "10px 20px",
                      border: "none",
                      borderRadius: "6px",
                      backgroundColor: "#16a34a",
                      color: "white",
                      fontSize: "16px",
                      fontWeight: "500",
                      cursor: "pointer",
                      textDecoration: "none",
                      display: "inline-block",
                      transition: "background-color 0.2s"
                    }}
                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = "#15803d"}
                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = "#16a34a"}
                  >
                    Create request email
                  </a>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
